import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  useTheme,
} from "@mui/material";
import { t } from "@lingui/core/macro";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import CreatePartnerCompanyDialog from "../partner/CreatePartnerCompanyDialog";
import {
  PartnerCompany,
  createPartnerApiClient,
  fetchPartnerCompanies,
  fetchPartnerContext,
  fetchPartnerLoginAsUrl,
  getCurrentTenantId,
  hasPartnerSession,
  applyPartnerLoginUrl,
  restorePartnerSession,
} from "../partner/partnerApi";

const PARTNER_ACCOUNT_VALUE = "__partner_account__";
const CREATE_COMPANY_VALUE = "__create_company__";
const COMPANY_MENU_VISIBLE_ROWS = 5;
const COMPANY_MENU_ITEM_HEIGHT = 36;
const COMPANY_MENU_STATIC_HEIGHT = 110;

const PartnerCompanySwitcher: React.FC = () => {
  const theme = useTheme();
  const [companies, setCompanies] = useState<PartnerCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [visible, setVisible] = useState(false);
  const [partnerName, setPartnerName] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [companySearch, setCompanySearch] = useState("");

  const currentTenantId = getCurrentTenantId();
  const hasStoredPartnerSession = hasPartnerSession();
  const normalizedCompanySearch = companySearch.trim().toLowerCase();
  const companyMenuMaxHeight =
    COMPANY_MENU_VISIBLE_ROWS * COMPANY_MENU_ITEM_HEIGHT +
    COMPANY_MENU_STATIC_HEIGHT;
  const filteredCompanies = normalizedCompanySearch
    ? companies.filter((company) =>
        company.name.toLowerCase().includes(normalizedCompanySearch),
      )
    : companies;

  const fetchPartnerData = React.useCallback(async () => {
    const partnerApi = createPartnerApiClient();
    try {
      setCompanies(await fetchPartnerCompanies(partnerApi));
      setVisible(true);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        // Partner token expired — clean up and hide switcher
        localStorage.removeItem("partnerToken");
      }
      setVisible(false);
      setLoading(false);
      return;
    }

    try {
      const partner = await fetchPartnerContext(partnerApi);
      setPartnerName(partner?.name ?? null);
    } catch {
      setPartnerName(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartnerData();
  }, [fetchPartnerData]);

  // Restore the partner account, open create company, or switch into a selected company tenant.
  const handleSwitch = async (value: string) => {
    if (value === currentTenantId || switching) return;

    if (value === PARTNER_ACCOUNT_VALUE) {
      if (restorePartnerSession()) {
        window.location.reload();
        return;
      }
    }

    if (value === CREATE_COMPANY_VALUE) {
      setCreateDialogOpen(true);
      return;
    }

    // Request a login-as URL for the selected company, store the returned token
    // as the active session, then reload so the app boots in that company context.
    setSwitching(true);
    try {
      const partnerApi = createPartnerApiClient();
      const url = await fetchPartnerLoginAsUrl(partnerApi, value);
      if (url && applyPartnerLoginUrl(url)) {
        window.location.reload();
        return;
      }
    } catch {
      // Silently fail — user stays on current company
    }
    setSwitching(false);
  };

  if (!visible) return null;

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1} py="6px">
        <CircularProgress size={16} />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1 }}>
      <Select
        value={currentTenantId ?? ""}
        onChange={(e) => handleSwitch(e.target.value as string)}
        onClose={() => setCompanySearch("")}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) {
            return partnerName || i18n._(t`Partner account`);
          }

          const currentCompany = companies.find(
            (company) => company.tenant_id === selected,
          );
          return currentCompany?.name ?? selected;
        }}
        size="small"
        fullWidth
        disabled={switching}
        sx={{
          fontSize: "1rem",
          backgroundColor: "rgba(0, 0, 0, 0.16)",
          borderRadius: "14px",
          minHeight: "36px",
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "& .MuiSelect-select": {
            py: "8px",
            px: "14px",
            textAlign: "left",
          },
          "& .MuiSelect-icon": {
            color: theme.palette.common.black,
          },
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          PaperProps: {
            sx: {
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.text.primary,
              maxWidth: 260,
              maxHeight: `${companyMenuMaxHeight}px`,
              overflowY: "auto",
              mt: "-4px",
              border: `1px solid ${theme.palette.divider}`,
              padding: theme.spacing(1),
              borderRadius: theme.shape.borderRadius * 1.5,
              boxShadow: "0px 4px 10px rgba(0,0,0,0.25)",
              "& .MuiList-root": {
                padding: 0,
              },
            },
          },
          MenuListProps: {
            autoFocusItem: false,
            sx: {
              padding: 0,
            },
          },
        }}
      >
        {hasStoredPartnerSession && (
          <MenuItem
            value={PARTNER_ACCOUNT_VALUE}
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.pxToRem(14),
              minHeight: 20,
              py: 1,
              borderRadius: theme.shape.borderRadius / 2,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.primary.main,
              },
            }}
          >
            {partnerName || i18n._(t`Partner account`)}
          </MenuItem>
        )}
        <ListSubheader
          disableSticky
          sx={{
            bgcolor: "transparent",
            px: 0,
            py: 0.5,
          }}
        >
          <TextField
            value={companySearch}
            onChange={(event) => setCompanySearch(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            onKeyUp={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            size="small"
            autoFocus
            fullWidth
            placeholder="Search"
            inputProps={{
              "aria-label": "Search companies",
            }}
            sx={{
              px: 0.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: "999px",
                backgroundColor: theme.palette.common.white,
                fontSize: theme.typography.pxToRem(13),
                minHeight: 34,
                "& fieldset": {
                  border: "none",
                },
              },
              "& .MuiOutlinedInput-input": {
                py: "8px",
                px: "14px",
              },
            }}
          />
        </ListSubheader>
        {filteredCompanies.map((company) => (
          <MenuItem
            key={company.tenant_id}
            value={company.tenant_id}
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.pxToRem(14),
              minHeight: 20,
              py: 1,
              borderRadius: theme.shape.borderRadius / 2,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.primary.main,
              },
            }}
          >
            {company.name}
          </MenuItem>
        ))}
        <MenuItem
          value={CREATE_COMPANY_VALUE}
          sx={{
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.pxToRem(14),
            minHeight: 20,
            py: 1,
            mt: 0.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius / 2,
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.primary.main,
            },
          }}
        >
          <Trans context="Partner portal button and dialog title for creating a company">
            Create Company
          </Trans>
        </MenuItem>
      </Select>
      <CreatePartnerCompanyDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={fetchPartnerData}
      />
    </Box>
  );
};

export default PartnerCompanySwitcher;
